import moment from 'moment';
import { CommerceNoteResults, CommerceNoteSearchOptions, CommerceNoteSearchParams } from '@atlassian/cssxp-types-audit';
import {
  Credit,
  ServiceCreditRequest,
  Invoice,
  InvoiceSearchOptions,
  InvoiceSearchParams,
  InvoiceWithLineItems,
  RefundInvoiceRequest,
  UpdateInvoiceRequest,
  InvoiceRequest,
  GenerateInvoiceRequest,
  GenerateInvoiceResponse,
} from '@atlassian/cssxp-types-billing';
import { Results } from '@atlassian/cssxp-typescommon';
import { createAxiosClient, ServiceProxyMap } from '@atlassian/temp-client-utils';
const client = createAxiosClient({ baseURL: '/api/1/invoice' }, ServiceProxyMap.ANS);

export async function searchInvoices(
  searchParams: InvoiceSearchParams & InvoiceSearchOptions
): Promise<Results<Invoice>> {
  const result = await client.get('/', {
    params: searchParams,
  });

  return result.data;
}

export async function fetchInvoice(invoiceId: string): Promise<InvoiceWithLineItems> {
  const result = await client.get(`/${invoiceId}`);

  return result.data;
}

export async function updateInvoiceWithMemo(invoiceRequest: UpdateInvoiceRequest): Promise<void> {
  const result = await client.patch(`/${invoiceRequest.invoiceId}`, {
    transactionAccountId: invoiceRequest.transactionAccountId,
    memo: invoiceRequest.memo,
  });
  return result.data;
}

export async function resendInvoice(invoiceRequest: UpdateInvoiceRequest): Promise<void> {
  const result = await client.post(`/${invoiceRequest.invoiceId}/sendemail`, {
    transactionAccountId: invoiceRequest.transactionAccountId,
  });
  return result.data;
}

export async function markUncollectible(invoiceRequest: InvoiceRequest): Promise<void> {
  const result = await client.post(`/${invoiceRequest.invoiceId}/uncollectible`, {
    transactionAccountId: invoiceRequest.transactionAccountId,
  });
  return result.data;
}

export async function generateInvoices(invoiceRequest: GenerateInvoiceRequest): Promise<GenerateInvoiceResponse> {
  const result = await client.post(`/generateInvoices`, {
    transactionAccountId: invoiceRequest.transactionAccountId,
    invoiceGroupId: invoiceRequest.invoiceGroupId,
  });
  return result.data;
}

export interface OrderStatus {
  completed: boolean;
  successful: boolean;
  errorMessage?: string;
  status: string;
  orderId: string;
  orderReference: string;
}

export async function fetchOrderStatus(invoiceId: string) {
  const result = await client.get<OrderStatus>(`/${invoiceId}/status`);

  return result.data;
}

export async function pollOrderStatusUntilComplete(invoiceId: string, interval = 1000, pollingTimeout = 60000) {
  const startTime = moment();

  const poller = async (resolve: (status: OrderStatus) => void, reject: (error: Error) => void) => {
    try {
      const invoiceStatus = await exports.fetchOrderStatus(invoiceId);

      if (invoiceStatus.completed) {
        resolve(invoiceStatus);
      } else if (moment().diff(startTime, 'ms') < pollingTimeout) {
        setTimeout(poller, interval, resolve, reject);
      } else {
        reject(new Error('Polling timeout'));
      }
    } catch (e: any) {
      reject(e);
    }
  };

  return new Promise<OrderStatus>(poller);
}

export async function createCredit({ invoiceId, ...creditRequest }: ServiceCreditRequest) {
  const result = await client.post<Credit>(`/${invoiceId}/credits`, creditRequest);

  return result.data;
}

export async function processRefund(invoiceId: string, requestPayload: RefundInvoiceRequest): Promise<void> {
  const result = await client.post(`/${invoiceId}/refund`, requestPayload);
  return result.data;
}

export async function searchCommerceNotes(
  invoiceKey: string,
  params: Omit<CommerceNoteSearchParams, 'invoiceKey'> & CommerceNoteSearchOptions
): Promise<CommerceNoteResults> {
  const response = await client.get(`/${encodeURIComponent(invoiceKey)}/notes`, {
    params,
  });
  return response.data;
}

export const fetchRefundActions = async (invoiceId: string) => {
  const response = await client.get(`${encodeURIComponent(invoiceId)}/refund/actions`);
  return response.data;
};

export async function getInvoicePdfData(
  invoiceId: string,
  transactionAccountId: string | null,
  sourceSystem?: string | undefined
) {
  const response = await client.get(`/${invoiceId}/download`, {
    params: {
      sourceSystem,
      transactionAccountId,
    },
  });
  if (response && response.data && response.data.successful) {
    return response.data.base64String;
  }
}

export async function downloadInvoicePdf(
  invoiceId: string,
  transactionAccountId: string | null,
  sourceSystem?: string
): Promise<string | null> {
  let fileUrl = null;
  try {
    const invoicePdfBase64 = await getInvoicePdfData(invoiceId, transactionAccountId, sourceSystem);
    if (invoicePdfBase64) {
      const blob = invoicePdfBase64 ? base64ToBlob(invoicePdfBase64) : null;
      fileUrl = blob ? (window.URL || window.webkitURL).createObjectURL(blob) : null;
    }
    return fileUrl;
  } catch {
    return fileUrl;
  }
}

export const base64ToBlob = (base64: string) => {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; ++i) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const blobBytes = new Blob([bytes], { type: 'application/pdf' });
  return blobBytes;
};
